import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { IcoBuilding, IcoPlus, IcoCheck, IcoClose, IcoTrash } from '../dashboard/DashIcons';
import ConfirmDeleteModal from '../modals/ConfirmDeleteModal';
import '../../styles/admin-dashboard.css';

function SetupDepartments() {
  const { user} = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDepartment, setShowAddDepartment] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [viewEmployeesModal, setViewEmployeesModal] = useState(null);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', color: '#22c55e' });
  
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [newDepartment, setNewDepartment] = useState({
    name: '',
    description: '',
    color: '#22c55e'
  });

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  useEffect(() => {
    // Refetch whenever we navigate to this page
    fetchDepartments();
    fetchEmployees();
    fetchManagers();
  }, [location.key]);

  const fetchDepartments = async () => {
    try {
      const storedUser = localStorage.getItem('user');
      const token = storedUser ? JSON.parse(storedUser).token : null;
      const userData = storedUser ? JSON.parse(storedUser) : null;
      const organizationId = userData?.organizationId;
      
      const response = await fetch(`/api/departments?organizationId=${organizationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDepartments(data);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const storedUser = localStorage.getItem('user');
      const token = storedUser ? JSON.parse(storedUser).token : null;
      const userData = storedUser ? JSON.parse(storedUser) : null;
      const organizationId = userData?.organizationId;
      
      const response = await fetch(`/api/staff?role=employee&organizationId=${organizationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEmployees(data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchManagers = async () => {
    try {
      const storedUser = localStorage.getItem('user');
      const token = storedUser ? JSON.parse(storedUser).token : null;
      const userData = storedUser ? JSON.parse(storedUser) : null;
      const organizationId = userData?.organizationId;
      
      const response = await fetch(`/api/staff?role=manager&organizationId=${organizationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setManagers(data);
      }
    } catch (error) {
      console.error('Error fetching managers:', error);
    }
  };

  const handleCreateDepartment = async (e) => {
    e.preventDefault();
    
    try {
      const storedUser = localStorage.getItem('user');
      const token = storedUser ? JSON.parse(storedUser).token : null;
      const userData = storedUser ? JSON.parse(storedUser) : null;
      const organizationId = userData?.organizationId;
      
      const response = await fetch('/api/departments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newDepartment,
          organizationId
        })
      });

      if (response.ok) {
        alert('✅ Department created successfully!');
        setShowAddDepartment(false);
        setNewDepartment({ name: '', description: '', color: '#22c55e' });
        fetchDepartments();
      } else {
        const data = await response.json();
        alert(`❌ Error: ${data.message}`);
      }
    } catch (error) {
      console.error('Error creating department:', error);
      alert('❌ Error creating department');
    }
  };

  const handleAssignEmployee = async (employeeId, departmentId) => {
    try {
      const storedUser = localStorage.getItem('user');
      const token = storedUser ? JSON.parse(storedUser).token : null;
      
      const response = await fetch(`/api/staff/${employeeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ departmentId })
      });

      if (response.ok) {
        try {
          const bc = new BroadcastChannel('dept-updates');
          bc.postMessage('department-updated');
          bc.close();
        } catch {}
        alert('✅ Employee assigned to department!');
        fetchEmployees();
      } else {
        alert('❌ Error assigning employee');
      }
    } catch (error) {
      console.error('Error assigning employee:', error);
      alert('❌ Error assigning employee');
    }
  };

  const handleUnassignEmployee = async (employeeId, departmentId, employeeName) => {
    if (!window.confirm(`Remove ${employeeName} from this department?`)) return;

    try {
      const storedUser = localStorage.getItem('user');
      const token = storedUser ? JSON.parse(storedUser).token : null;
      
      const employee = employees.find(e => e._id === employeeId);
      if (!employee) return;
      
      const updatedDepartmentIds = employee.departmentIds.filter(id => id !== departmentId);
      
      const response = await fetch(`/api/staff/${employeeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ departmentIds: updatedDepartmentIds })
      });

      if (response.ok) {
        try {
          const bc = new BroadcastChannel('dept-updates');
          bc.postMessage('department-updated');
          bc.close();
        } catch {}
        alert('✅ Employee unassigned from department!');
        fetchEmployees();
      } else {
        alert('❌ Error unassigning employee');
      }
    } catch (error) {
      console.error('Error unassigning employee:', error);
      alert('❌ Error unassigning employee');
    }
  };

  const handleDeleteDepartment = async (departmentId, departmentName) => {

    try {
      const storedUser = localStorage.getItem('user');
      const token = storedUser ? JSON.parse(storedUser).token : null;
      
      const response = await fetch(`/api/departments/${departmentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('✅ Department deleted!');
        fetchDepartments();
      } else {
        alert('❌ Error deleting department');
      }
    } catch (error) {
      console.error('Error deleting department:', error);
      alert('❌ Error deleting department');
    }
  };

  const handleUpdateDepartment = async (e) => {
    e.preventDefault();
    try {
      const storedUser = localStorage.getItem('user');
      const token = storedUser ? JSON.parse(storedUser).token : null;

      const response = await fetch(`/api/departments/${editingDepartment}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        setEditingDepartment(null);
        fetchDepartments();
      } else {
        const data = await response.json();
        alert(`❌ Error: ${data.message}`);
      }
    } catch (error) {
      console.error('Error updating department:', error);
      alert('❌ Error updating department');
    }
  };

  const handleDeleteEmployee = async (employeeId, employeeName) => {

    try {
      const storedUser = localStorage.getItem('user');
      const token = storedUser ? JSON.parse(storedUser).token : null;
      
      const response = await fetch(`/api/staff/${employeeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('✅ Employee deleted!');
        fetchEmployees();
      } else {
        alert('❌ Error deleting employee');
      }
    } catch (error) {
      console.error('Error deleting employee:', error);
      alert('❌ Error deleting employee');
    }
  };

  const getDepartmentEmployees = (departmentId) => {
    return employees.filter(emp => 
      emp.departmentIds && emp.departmentIds.includes(departmentId)
    );
  };

  const getDepartmentManagers = (departmentId) => {
    return managers.filter(mgr => 
      mgr.departmentIds && mgr.departmentIds.includes(departmentId)
    );
  };

  if (!user) return null;

  const colors = [
    '#22c55e', '#f59e0b', '#10b981', '#22c55e', '#ef4444', 
    '#8b5cf6', '#ec4899', '#22c55e', '#84cc16', '#f97316'
  ];

  return (
    <div className="admin-dashboard">
      <div className="dashboard-content">
        <section className="welcome-section">
          <h1 style={{display:'flex',alignItems:'center',gap:'0.5rem'}}><IcoBuilding size={24} color="#22c55e"/> Setup Departments</h1>
          <p className="subtitle">Create departments and assign employees to them</p>
        </section>

        <div style={{ marginBottom: '2rem' }}>
          <button
            onClick={() => setShowAddDepartment(!showAddDepartment)}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '1rem'
            }}
          >
            <><IcoPlus size={14}/> Add New Department</>
          </button>
        </div>

        {showAddDepartment && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '2rem',
            marginBottom: '2rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ marginBottom: '1.5rem', fontSize: '1.25rem', fontWeight: '700' }}>
              Create New Department
            </h2>
            
            <form onSubmit={handleCreateDepartment} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <label style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ marginBottom: '0.5rem', fontWeight: '500' }}>Department Name*</span>
                  <input
                    type="text"
                    value={newDepartment.name}
                    onChange={(e) => setNewDepartment(prev => ({ ...prev, name: e.target.value }))}
                    required
                    placeholder="e.g., Engineering, Sales, HR"
                    style={{
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px'
                    }}
                  />
                </label>

                <label style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ marginBottom: '0.5rem', fontWeight: '500' }}>Color</span>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {colors.map(color => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setNewDepartment(prev => ({ ...prev, color }))}
                        style={{
                          width: '2rem',
                          height: '2rem',
                          background: color,
                          border: newDepartment.color === color ? '3px solid #111827' : '1px solid #d1d5db',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      />
                    ))}
                  </div>
                </label>
              </div>

              <label style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ marginBottom: '0.5rem', fontWeight: '500' }}>Description</span>
                <textarea
                  value={newDepartment.description}
                  onChange={(e) => setNewDepartment(prev => ({ ...prev, description: e.target.value }))}
                  rows="3"
                  placeholder="Optional description"
                  style={{
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    resize: 'vertical'
                  }}
                />
              </label>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button
                  type="submit"
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Create Department
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddDepartment(false)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#4b5563',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Departments List */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '700' }}>
            Departments ({departments.length})
          </h2>

          {loading ? (
            <p>Loading departments...</p>
          ) : departments.length === 0 ? (
            <p style={{ color: '#6b7280' }}>No departments yet. Create one above!</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {departments.map((dept) => {
                const assignedEmployees = getDepartmentEmployees(dept._id);
                const assignedManagers = getDepartmentManagers(dept._id);
                
                return (
                  <div
                    key={dept._id}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '1.5rem'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div
                          style={{
                            width: '1rem',
                            height: '1rem',
                            background: dept.color,
                            borderRadius: '4px'
                          }}
                        />
                        <div>
                          <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.25rem' }}>
                            {dept.name}
                          </h3>
                          <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                            {dept.description && (
                              <ul style={{ margin: '0 0 0.25rem', paddingLeft: '1.1rem' }}>
                                {dept.description.split('\n').filter(l => l.trim()).map((line, i) => (
                                  <li key={i}>{line.trim()}</li>
                                ))}
                              </ul>
                            )}
                            <span>{assignedManagers.length} {assignedManagers.length === 1 ? 'manager' : 'managers'} • {assignedEmployees.length} {assignedEmployees.length === 1 ? 'employee' : 'employees'}</span>
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => setViewEmployeesModal(dept._id)}
                          style={{ padding: '0.5rem 1rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                        >
                          View Employees
                        </button>
                        <button
                          onClick={() => {
                            if (editingDepartment === dept._id) {
                              setEditingDepartment(null);
                            } else {
                              setEditingDepartment(dept._id);
                              setEditForm({ name: dept.name, description: dept.description || '', color: dept.color });
                            }
                          }}
                          style={{ padding: '0.5rem 1rem', background: editingDepartment === dept._id ? '#f59e0b' : '#22c55e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                        >
                          {editingDepartment === dept._id ? 'Cancel' : 'Edit'}
                        </button>
                        <button
                          onClick={() => setSelectedDepartment(selectedDepartment === dept._id ? null : dept._id)}
                          style={{ padding: '0.5rem 1rem', background: selectedDepartment === dept._id ? '#22c55e' : '#f3f4f6', color: selectedDepartment === dept._id ? 'white' : '#374151', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                        >
                          {selectedDepartment === dept._id ? 'Hide' : 'Manage'}
                        </button>
                        <button
                          onClick={() => setConfirmDelete({ type: 'department', id: dept._id, name: dept.name })}
                          style={{ padding: '0.5rem 1rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                        >
                          <IcoTrash size={14} color="white"/>
                        </button>
                      </div>
                    </div>

                    {editingDepartment === dept._id && (
                      <form onSubmit={handleUpdateDepartment} style={{ marginTop: '1rem', padding: '1.25rem', background: '#f0f4ff', borderRadius: '8px', border: '1px solid #c7d2fe', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                            <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>Department Name*</span>
                            <input
                              type="text"
                              value={editForm.name}
                              onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                              required
                              style={{ padding: '0.65rem', border: '1.5px solid #c7d2fe', borderRadius: '6px', fontSize: '0.95rem' }}
                            />
                          </label>
                          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                            <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>Color</span>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', paddingTop: '0.25rem' }}>
                              {colors.map(color => (
                                <button key={color} type="button" onClick={() => setEditForm(prev => ({ ...prev, color }))}
                                  style={{ width: '2rem', height: '2rem', background: color, border: editForm.color === color ? '3px solid #111827' : '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer' }}
                                />
                              ))}
                            </div>
                          </label>
                        </div>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                          <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>Description</span>
                          <textarea
                            rows={3}
                            value={editForm.description}
                            onChange={e => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="One address or item per line"
                            style={{ padding: '0.65rem', border: '1.5px solid #c7d2fe', borderRadius: '6px', fontSize: '0.95rem', resize: 'vertical' }}
                          />
                        </label>
                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                          <button type="submit" style={{ padding: '0.6rem 1.25rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
                            Save Changes
                          </button>
                          <button type="button" onClick={() => setEditingDepartment(null)} style={{ padding: '0.6rem 1.25rem', background: '#4b5563', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}

                    {selectedDepartment === dept._id && (
                      <div style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        background: '#f9fafb',
                        borderRadius: '6px'
                      }}>
                        <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem' }}>
                          All Employees:
                        </h4>
                        {employees.length === 0 ? (
                          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>No employees found</p>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {employees.map(emp => {
                              const displayName = emp.firstName && emp.lastName 
                                ? `${emp.firstName} ${emp.lastName}`
                                : emp.email || `Employee ${emp._id.slice(-4)}`;
                              
                              const isAssigned = emp.departmentIds && emp.departmentIds.includes(dept._id);
                              
                              return (
                                <div
                                  key={emp._id}
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '0.5rem',
                                    background: isAssigned ? '#e0e7ff' : 'white',
                                    borderRadius: '4px',
                                    border: isAssigned ? '1px solid #c7d2fe' : 'none'
                                  }}
                                >
                                  <span style={{ fontWeight: isAssigned ? '600' : 'normal', color: isAssigned ? '#4338ca' : 'inherit' }}>
                                    {displayName} {isAssigned && <IcoCheck size={12} color="#22c55e"/>}
                                  </span>
                                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    {isAssigned ? (
                                      <button
                                        onClick={() => handleUnassignEmployee(emp._id, dept._id, displayName)}
                                        style={{
                                          padding: '0.25rem 0.75rem',
                                          background: '#ef4444',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          cursor: 'pointer',
                                          fontSize: '0.75rem',
                                          fontWeight: '600'
                                        }}
                                      >
                                        Unassign
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => handleAssignEmployee(emp._id, dept._id)}
                                        style={{
                                          padding: '0.25rem 0.75rem',
                                          background: '#10b981',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          cursor: 'pointer',
                                          fontSize: '0.75rem',
                                          fontWeight: '600'
                                        }}
                                      >
                                        Assign
                                      </button>
                                    )}
                                    <button
                                      onClick={() => setConfirmDelete({ type: 'employee', id: emp._id, name: displayName })}
                                      style={{
                                        padding: '0.25rem 0.75rem',
                                        background: '#6b7280',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '0.75rem',
                                        fontWeight: '600',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '0.3rem'
                                      }}
                                    >
                                      <IcoTrash size={12} color="white"/>
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>

      {/* View Employees Modal */}
      {viewEmployeesModal && (() => {
        const department = departments.find(d => d._id === viewEmployeesModal);
        const assignedEmployees = getDepartmentEmployees(viewEmployeesModal);
        
        return (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '2rem'
            }}
            onClick={() => setViewEmployeesModal(null)}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '2rem',
                maxWidth: '800px',
                width: '100%',
                maxHeight: '80vh',
                overflowY: 'auto',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '2px solid #e5e7eb', paddingBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ width: '1.5rem', height: '1.5rem', background: department?.color, borderRadius: '4px' }} />
                  <div>
                    <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.25rem' }}>
                      {department?.name}
                    </h2>
                    <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                      {assignedEmployees.length} {assignedEmployees.length === 1 ? 'employee' : 'employees'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setViewEmployeesModal(null)}
                  style={{
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '0.5rem',
                    cursor: 'pointer',
                    fontSize: '1.25rem',
                    lineHeight: '1',
                    color: '#6b7280'
                  }}
                >
                  <IcoClose size={16}/>
                </button>
              </div>

              {assignedEmployees.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                  <p style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>No employees assigned yet</p>
                  <p style={{ fontSize: '0.875rem' }}>Click "Manage" to assign employees</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {assignedEmployees.map(emp => {
                    const displayName = emp.firstName && emp.lastName 
                      ? `${emp.firstName} ${emp.lastName}`
                      : emp.email || `Employee ${emp._id.slice(-4)}`;
                    
                    return (
                      <div
                        key={emp._id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '1rem',
                          background: '#f9fafb',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb'
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: '700', fontSize: '1rem', marginBottom: '0.25rem' }}>
                            {displayName}
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                            {emp.email} • ${emp.hourlyRate}/hr
                          </div>
                        </div>
                        
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => {
                              navigate(`/employee/${emp._id}`);
                              setViewEmployeesModal(null);
                            }}
                            style={{
                              padding: '0.5rem 0.75rem',
                              background: '#22c55e',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              fontWeight: '600'
                            }}
                          >
                            View Profile
                          </button>
                          
                          <button
                            onClick={() => {
                              navigate(`/team?edit=${emp._id}`);
                              setViewEmployeesModal(null);
                            }}
                            style={{
                              padding: '0.5rem 0.75rem',
                              background: '#8b5cf6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              fontWeight: '600'
                            }}
                          >
                            Edit
                          </button>
                          
                          <button
                            onClick={() => handleUnassignEmployee(emp._id, department._id, displayName)}
                            style={{
                              padding: '0.5rem 0.75rem',
                              background: '#f59e0b',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              fontWeight: '600'
                            }}
                          >
                            Unassign
                          </button>
                          
                          <button
                            onClick={() => setConfirmDelete({ type: 'employee', id: emp._id, name: displayName })}
                            style={{
                              padding: '0.5rem 0.75rem',
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.35rem'
                            }}
                          >
                            <IcoTrash size={13} color="white"/>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {confirmDelete && (
        <ConfirmDeleteModal
          message={
            confirmDelete.type === 'department'
              ? `Are you sure you want to delete the "${confirmDelete.name}" department? This cannot be undone.`
              : `Are you sure you want to delete ${confirmDelete.name}? This cannot be undone.`
          }
          onConfirm={() => {
            if (confirmDelete.type === 'department') handleDeleteDepartment(confirmDelete.id, confirmDelete.name);
            else handleDeleteEmployee(confirmDelete.id, confirmDelete.name);
            setConfirmDelete(null);
          }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

export default SetupDepartments;